(() => {
  if (window.CarniBrandStory) { window.CarniBrandStory.mount(document); return; }
  const active = new Map();
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = value => Math.max(0, Math.min(1, value));
  const momentObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-seen');
        momentObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: .18 }) : null;
  let pending = false;
  function render() {
    pending = false;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - innerHeight);
    for (const [root, frame] of active) {
      if (!root.isConnected) { active.delete(root); continue; }
      let wipe = 1;
      const held = root.querySelector('[data-brand-pin]');
      if (held && (reduce.matches || root.dataset.motion === 'false')) held.style.transform = '';
      if (!reduce.matches && root.dataset.motion !== 'false') {
        const track = root.querySelector('[data-brand-track]');
        const pin = root.querySelector('[data-brand-pin]');
        if (track && pin) {
          // Pinned: progress is how far the track has travelled past the pin's
          // sticking point, over the extra height the track carries.
          // The pin is held with a transform, not position:sticky: sticky would not
          // engage inside this theme's wrapper (measured, 2026-09-21).
          const travel = Math.max(1, track.offsetHeight - pin.offsetHeight);
          // Inside the one-time modal the scroller is the panel, not the window, so the
          // holding line is measured from the panel's top edge.
          const scroller = root.closest('[data-brand-scroll]');
          const stick = scroller
            ? scroller.getBoundingClientRect().top + 28
            : parseFloat(getComputedStyle(root).getPropertyValue('--brand-stick')) || 120;
          const past = Math.max(0, Math.min(travel, stick - track.getBoundingClientRect().top));
          pin.style.transform = `translate3d(0,${past}px,0)`;
          wipe = clamp(past / (travel * .85));
        } else {
          const top = frame.getBoundingClientRect().top + scrollY;
          const start = Math.max(0, top - innerHeight * .9);
          const end = Math.min(maxScroll, Math.max(start + 180, top - innerHeight * .48));
          wipe = end <= start ? 1 : clamp((scrollY - start) / (end - start));
        }
      }
      root.style.setProperty('--brand-clip', `${(1 - wipe) * 100}%`);
      root.style.setProperty('--brand-hint', String(1 - clamp(wipe / .25)));
      root.style.setProperty('--brand-edge', wipe > .02 && wipe < .97 ? '1' : '0');
      root.style.setProperty('--brand-old', String(1 - clamp((wipe - .7) / .3)));
    }
  }
  function queue() { if (!pending) { pending = true; requestAnimationFrame(render); } }
  function toggleAppearance() {
    const sharedToggle = document.querySelector('.header__icons .carni-theme-toggle');
    if (sharedToggle) { sharedToggle.click(); return; }
    const html = document.documentElement;
    const next = html.getAttribute('data-carni-theme') === 'dark' ? 'light' : 'dark';
    html.setAttribute('data-carni-theme', next);
    try { localStorage.setItem('carni-theme', next); } catch (error) {}
  }
  // One-time modal. The key carries the banner id, so dismissing this announcement
  // never hides a future one. Storage can be blocked (private mode): then it simply
  // shows again next visit rather than breaking.
  const seenKey = root => 'carni-brand-seen:' + (root.dataset.bannerId || 'default');
  let lastFocus = null;
  function openModal(modal) {
    const story = modal.querySelector('[data-brand-story]');
    try { if (story && localStorage.getItem(seenKey(story))) return; } catch (error) {}
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('carni-brand-lock');
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      const close = modal.querySelector('.carni-brand-modal__close');
      if (close) close.focus({ preventScroll: true });
      queue();
    });
    const scroller = modal.querySelector('[data-brand-scroll]');
    if (scroller) scroller.addEventListener('scroll', queue, { passive: true });
  }
  function closeModal(modal) {
    const story = modal.querySelector('[data-brand-story]');
    try { if (story) localStorage.setItem(seenKey(story), String(Date.now())); } catch (error) {}
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('carni-brand-lock');
    setTimeout(() => { modal.hidden = true; }, 300);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }
  document.addEventListener('click', event => {
    const hit = event.target.closest && event.target.closest('[data-brand-dismiss]');
    if (hit) closeModal(hit.closest('[data-brand-modal]'));
  });
  document.addEventListener('keydown', event => {
    const modal = document.querySelector('[data-brand-modal].is-open');
    if (!modal) return;
    if (event.key === 'Escape') { closeModal(modal); return; }
    if (event.key !== 'Tab') return;
    // Keep Tab inside the dialog.
    const items = [...modal.querySelectorAll('button, a[href]')].filter(el => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  function mount(scope) {
    scope.querySelectorAll('[data-brand-story]').forEach(root => {
      const frame = root.querySelector('.carni-brand-story__frame');
      if (frame) active.set(root, frame);
      root.querySelectorAll('[data-brand-moment]').forEach(moment => {
        if (reduce.matches || !momentObserver) moment.classList.add('is-seen');
        else momentObserver.observe(moment);
      });
    });
    // Straight away (owner, 2026-09-22): the announcement is the first thing a new visitor sees.
    scope.querySelectorAll('[data-brand-modal]').forEach(modal => openModal(modal));
    queue();
  }
  window.CarniBrandStory = { mount };
  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue, { passive: true });
  addEventListener('pageshow', queue);
  reduce.addEventListener('change', queue);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', queue);
  document.addEventListener('click', event => {
    const button = event.target.closest && event.target.closest('[data-brand-theme-toggle]');
    if (!button) return;
    event.preventDefault();
    toggleAppearance();
  });
  document.fonts.ready.then(queue);
  mount(document);
})();
